import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ChartLineUp,
  Users,
  Briefcase,
  Lightbulb,
  Handshake,
  Crown,
  GraduationCap,
  Target,
  Globe,
  CheckCircle,
  Quotes,
  ArrowUpRight,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const heroSlides = [
  {
    tag: "New Project",
    title: "New TVET School in",
    highlight: "Bombo, Luweero",
    description: "A state-of-the-art skills training center empowering youth through practical vocational education and entrepreneurship.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/afobkmcc_SCHOOL.png",
    cta: { text: "Explore TVET", link: "/tvet" },
    badge: "new",
  },
  {
    tag: "Coming Soon",
    title: "Community Healthcare",
    highlight: "Center \u2013 Bombo Town",
    description: "Improving access to quality healthcare, preventive care, and community wellness services for Bombo and surrounding areas.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/fgzorv0v_HP.png",
    cta: { text: "Learn More", link: "/about" },
    badge: "coming-soon",
  },
  {
    tag: "TVET & Skills Training",
    title: "Practical Skills That",
    highlight: "Transform Lives",
    description: "Equipping youth with hands-on technical and vocational skills for employment and entrepreneurship.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0tfpxi02_PP.png",
    cta: { text: "Our Programs", link: "/programs" },
  },
  {
    tag: "Enterprise Development",
    title: "Growing Sustainable",
    highlight: "African Enterprises",
    description: "Supporting small businesses and startups with training, mentorship, and market access to thrive.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/lxiebqhu_55d8f7e2-761d-4c63-9839-a0652352eb5a.jpeg",
    cta: { text: "Our Services", link: "/services" },
  },
  {
    tag: "Agribusiness Opportunities",
    title: "Unlocking Africa\u2019s",
    highlight: "Agricultural Potential",
    description: "Training farmers in modern practices, value chain development, and agribusiness for sustainable incomes.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/ymbseg05_AGRIC.png",
    cta: { text: "View Projects", link: "/projects" },
  },
  {
    tag: "Technology & Innovation",
    title: "Digital Skills for a",
    highlight: "Modern Economy",
    description: "Bridging the digital divide with ICT training, innovation hubs, and tech-enabled entrepreneurship.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0s4xigoz_ICT.png",
    cta: { text: "Programs", link: "/programs" },
  },
  {
    tag: "Youth & Women Empowerment",
    title: "Investing in Youth &",
    highlight: "Women-Led Growth",
    description: "Targeted programs that empower young people and women to become agents of change in their communities.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/5qwhppbf_youth-in-agriculture.jpg",
    cta: { text: "Get Involved", link: "/contact" },
  },
  {
    tag: "Partnerships for Impact",
    title: "Collaborate With Us for",
    highlight: "Lasting Change",
    description: "Join hands with PEDA AFRICA to create scalable, sustainable impact across communities.",
    image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/cnkb2n0r_prefects-body-Serere-Campus-scaled.jpg",
    cta: { text: "Partner With Us", link: "/partnerships" },
  },
];

const services = [
  {
    icon: ChartLineUp,
    title: "Empirical Research",
    text: "We carry out research to identify pressing community and enterprise needs, generating knowledge that guides practical and sustainable interventions.",
  },
  {
    icon: Users,
    title: "Capacity Building",
    text: "We equip local entrepreneurs, enterprises, and communities with practical skills, specialized training, and resources needed for growth and resilience.",
  },
  {
    icon: Briefcase,
    title: "Enterprise Development",
    text: "We support community-led enterprises in transforming innovative ideas into actionable projects and impactful real-world solutions.",
  },
  {
    icon: Lightbulb,
    title: "Technological Innovation",
    text: "We promote suitable and cutting-edge technologies that strengthen entrepreneurial ventures and improve productivity across sectors.",
  },
  {
    icon: Handshake,
    title: "Leadership Development",
    text: "Our leadership programs emphasize community ownership, active participation, and transformational leadership for long-term sustainability.",
  },
  {
    icon: Crown,
    title: "Business Support Services",
    text: "We provide training in business management, financial literacy, marketing development systems, and other essential skills for enterprise success.",
  },
];

const impactAreas = [
  "Community-Led Enterprises",
  "Local Entrepreneurs & Startups",
  "Youth & Women Empowerment",
  "Business Leadership Development",
  "Technology & Innovation",
  "TVET Programs",
];

export default function HomePage() {
  const [projects, setProjects] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideTimer = useRef(null);
  const totalSlides = heroSlides.length;

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  useEffect(() => {
    slideTimer.current = setInterval(nextSlide, 6000);
    return () => clearInterval(slideTimer.current);
  }, [nextSlide]);

  const resetTimer = useCallback(() => {
    clearInterval(slideTimer.current);
    slideTimer.current = setInterval(nextSlide, 6000);
  }, [nextSlide]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await fetch(`${BACKEND_URL}/api/seed`, { method: "POST" });
        
        // Fetch projects and blog posts
        const [projectsRes, blogRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/projects?status=ongoing`),
          fetch(`${BACKEND_URL}/api/blog?limit=3`)
        ]);
        
        const projectsData = await projectsRes.json();
        const blogData = await blogRes.json();
        
        setProjects(projectsData.slice(0, 3));
        setBlogPosts(blogData);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  return (
    <div>
      {/* Hero Slider */}
      <section className="relative h-[92vh] min-h-[600px] max-h-[900px] overflow-hidden" data-testid="hero-section">
        {/* Slide Backgrounds */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('${heroSlides[currentSlide].image}')` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-green-950/85 via-green-950/60 to-green-950/30" />
          </motion.div>
        </AnimatePresence>

        {/* Slide Content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="max-w-2xl"
              >
                <span className="inline-block px-4 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/25 text-amber-300 text-xs font-semibold uppercase tracking-wider mb-5">
                  {heroSlides[currentSlide].tag}
                </span>
                {heroSlides[currentSlide].badge === "new" && (
                  <span className="inline-block ml-2 px-3 py-1 rounded-full bg-green-500 text-white text-xs font-bold uppercase tracking-wider mb-5 animate-pulse">
                    New Project
                  </span>
                )}
                {heroSlides[currentSlide].badge === "coming-soon" && (
                  <span className="inline-block ml-2 px-3 py-1 rounded-full bg-cyan-500 text-white text-xs font-bold uppercase tracking-wider mb-5 animate-pulse">
                    Coming Soon
                  </span>
                )}
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-5">
                  {heroSlides[currentSlide].title}{" "}
                  <span className="text-amber-400">{heroSlides[currentSlide].highlight}</span>
                </h1>
                <p className="text-base sm:text-lg text-green-50/85 leading-relaxed mb-8 max-w-xl">
                  {heroSlides[currentSlide].description}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    asChild
                    size="lg"
                    className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-7 h-12 text-sm"
                    data-testid="hero-explore-programs-button"
                  >
                    <Link to={heroSlides[currentSlide].cta.link}>
                      {heroSlides[currentSlide].cta.text} <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="bg-white/10 hover:bg-white/20 border-white/25 text-white rounded-xl px-7 h-12 text-sm"
                    data-testid="hero-contact-button"
                  >
                    <Link to="/contact">Contact Us</Link>
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Slide Navigation */}
            <div className="mt-10 flex items-center gap-4">
              {/* Arrow Buttons */}
              <button
                onClick={() => { prevSlide(); resetTimer(); }}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors"
                aria-label="Previous slide"
                data-testid="hero-prev-button"
              >
                <CaretLeft className="h-5 w-5" weight="bold" />
              </button>

              {/* Dots */}
              <div className="flex items-center gap-2" data-testid="hero-slide-dots">
                {heroSlides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { goToSlide(i); resetTimer(); }}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      i === currentSlide ? "w-8 bg-amber-400" : "w-2 bg-white/40 hover:bg-white/60"
                    }`}
                    aria-label={`Go to slide ${i + 1}`}
                    data-testid={`hero-dot-${i}`}
                  />
                ))}
              </div>

              <button
                onClick={() => { nextSlide(); resetTimer(); }}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors"
                aria-label="Next slide"
                data-testid="hero-next-button"
              >
                <CaretRight className="h-5 w-5" weight="bold" />
              </button>

              {/* Slide Counter */}
              <span className="text-white/50 text-sm ml-2 tabular-nums">
                {String(currentSlide + 1).padStart(2, "0")} / {String(totalSlides).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
                About PEDA
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                A Research-Driven Organization Advancing Sustainable Development
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                PEDA AFRICA is dedicated to empowering communities through a multi-sectoral approach that prioritizes community-led enterprises as key drivers of change, prosperity, and resilience.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-100">
                  <Target className="h-6 w-6 text-green-700 shrink-0" weight="duotone" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Our Vision</h4>
                    <p className="text-sm text-slate-600">Africa with vibrant entrepreneurial ecosystems igniting inclusive growth.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-100">
                  <Globe className="h-6 w-6 text-green-700 shrink-0" weight="duotone" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Our Mission</h4>
                    <p className="text-sm text-slate-600">Building resilient, innovative communities that nurture entrepreneurship.</p>
                  </div>
                </div>
              </div>
              
              <Button 
                asChild 
                className="bg-green-700 hover:bg-green-800 rounded-xl px-6"
                data-testid="learn-more-about-button"
              >
                <Link to="/about">
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/cnkb2n0r_prefects-body-Serere-Campus-scaled.jpg" 
                  alt="PEDA Leadership"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-green-950/60 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <Quotes className="h-8 w-8 text-amber-500" weight="fill" />
                      <p className="text-sm text-slate-700 italic">
                        "Empowering local communities to lead their own development creates lasting and meaningful change."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-amber-500 rounded-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-28" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Our Core Capacities
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Comprehensive Interventions That Turn Ideas Into Impact
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Our work combines empirical research, targeted training, leadership development, innovation support, and enterprise strengthening.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white border border-slate-100 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center mb-6 group-hover:bg-green-700 transition-colors">
                  <service.icon className="h-7 w-7 text-green-700 group-hover:text-white transition-colors" weight="duotone" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed">{service.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              asChild 
              variant="outline" 
              className="rounded-xl px-6 border-green-700 text-green-700 hover:bg-green-50"
              data-testid="view-all-services-button"
            >
              <Link to="/services">
                View All Services <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* TVET Preview */}
      <section className="py-20 md:py-28 bg-green-950" data-testid="tvet-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">
                TVET Programs
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
                Technical & Vocational Education Training
              </h2>
              <p className="text-lg text-green-100/80 leading-relaxed mb-8">
                PEDA is committed to equipping youth and communities with practical, income-generating skills through TVET programs aligned with real market needs.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {[
                  "Entrepreneurship & Business Skills",
                  "ICT & Digital Skills Training",
                  "Agribusiness & Value Addition",
                  "Construction & Vocational Trades",
                  "Financial Literacy",
                  "Innovation & Startups",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
                    <CheckCircle className="h-5 w-5 text-amber-400 shrink-0" weight="fill" />
                    <span className="text-green-100 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                asChild 
                className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-6"
                data-testid="explore-tvet-button"
              >
                <Link to="/tvet">
                  Explore TVET Programs <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
                </Link>
              </Button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0tfpxi02_PP.png" 
                alt="TVET Training"
                className="w-full h-[450px] object-cover rounded-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-amber-500 rounded-2xl p-6 shadow-xl max-w-xs">
                <GraduationCap className="h-10 w-10 text-green-950 mb-3" weight="duotone" />
                <p className="text-green-950 font-semibold">Empowering individuals with hands-on skills that increase employability and promote self-reliance.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* School Project Highlight Section */}
      <section className="py-20 md:py-28 bg-amber-500" data-testid="school-project-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-green-950">
                  Featured Initiative
                </span>
                <span className="px-3 py-1 rounded-full bg-green-700 text-white text-xs font-bold uppercase">
                  New Project
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-green-950 tracking-tight mb-4">
                TVET School &ndash; Bombo
              </h2>
              <p className="text-sm font-semibold text-green-900 uppercase tracking-wider mb-4">
                Empowering Youth Through Skills &amp; Opportunity
              </p>
              <p className="text-base text-green-900/80 leading-relaxed mb-4">
                In Bombo, Luweero District, thousands of young people lack access to practical skills needed for employment and entrepreneurship. PEDA AFRICA is establishing a modern TVET School that will equip youth with hands-on skills for self-reliance.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-2.5 mb-6">
                {[
                  "ICT & Digital Skills",
                  "Carpentry & Construction",
                  "Tailoring & Fashion Design",
                  "Agribusiness & Farming",
                  "Entrepreneurship & Business",
                  "Train 300+ youth annually",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2.5 bg-white/20 backdrop-blur rounded-xl p-2.5">
                    <CheckCircle className="h-4 w-4 text-green-950 shrink-0" weight="fill" />
                    <span className="text-green-950 text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <p className="text-sm text-green-900/70 mb-6 italic">
                Your support can build classrooms, provide tools and equipment, and sponsor students from low-income families.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  asChild 
                  className="bg-green-700 hover:bg-green-800 text-white font-semibold rounded-xl px-6"
                  data-testid="support-schools-button"
                >
                  <Link to="/donate">
                    Support This Project <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
                  </Link>
                </Button>
                <Button 
                  asChild 
                  className="bg-white hover:bg-green-50 text-green-800 font-semibold rounded-xl px-6"
                  data-testid="sponsor-student-button"
                >
                  <Link to="/donate">Sponsor a Student</Link>
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/afobkmcc_SCHOOL.png" 
                alt="TVET School Bombo"
                className="w-full h-48 object-cover rounded-2xl shadow-lg col-span-2"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0s4xigoz_ICT.png" 
                alt="ICT Training Lab"
                className="w-full h-48 object-cover rounded-2xl shadow-lg"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/eghjahcl_PPD.png" 
                alt="PEDA Africa Production"
                className="w-full h-48 object-cover rounded-2xl shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Healthcare Center Section */}
      <section className="py-20 md:py-28 bg-green-950" data-testid="healthcare-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1 grid grid-cols-2 gap-4"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/fgzorv0v_HP.png" 
                alt="Community Healthcare Center Bombo"
                className="w-full h-48 object-cover rounded-2xl shadow-xl col-span-2"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/qkndwzxh_NR.png" 
                alt="Healthcare Training"
                className="w-full h-48 object-cover rounded-2xl shadow-xl"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0tfpxi02_PP.png" 
                alt="Community Programs"
                className="w-full h-48 object-cover rounded-2xl shadow-xl"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">
                  Upcoming Initiative
                </span>
                <span className="px-3 py-1 rounded-full bg-cyan-500 text-white text-xs font-bold uppercase animate-pulse">
                  Coming Soon
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
                Community Healthcare Center &ndash; Bombo Town
              </h2>
              <p className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-4">
                Bringing Healthcare Closer to the Community
              </p>
              <p className="text-base text-green-100/80 leading-relaxed mb-4">
                In many parts of Bombo, access to quality healthcare remains limited. Families travel long distances for basic medical services. PEDA AFRICA is launching a Community Healthcare Center to change this.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-2.5 mb-6">
                {[
                  "Primary healthcare services",
                  "Maternal & child health support",
                  "Preventive care & health education",
                  "Basic diagnostics & treatment",
                  "Community health outreach",
                  "Serve thousands annually",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2.5 bg-white/5 rounded-xl p-2.5">
                    <CheckCircle className="h-4 w-4 text-amber-400 shrink-0" weight="fill" />
                    <span className="text-green-100 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <p className="text-sm text-green-200/60 mb-6 italic">
                Your donation will help construct and equip the facility, provide medical supplies, and support healthcare training programs.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  asChild 
                  className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-6"
                  data-testid="support-healthcare-button"
                >
                  <Link to="/donate">
                    Fund Healthcare Access <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
                  </Link>
                </Button>
                <Button 
                  asChild 
                  variant="outline"
                  className="bg-white/10 hover:bg-white/20 border-white/25 text-white rounded-xl px-6"
                  data-testid="learn-more-healthcare-button"
                >
                  <Link to="/contact">Get In Touch</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="success-stories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Impact Stories
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Transforming Lives Across Communities
            </h2>
            <p className="text-lg text-slate-600">
              See how PEDA programs are creating lasting change in the lives of community members across Uganda.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/e0f592gf_11.jpg",
                title: "Farmers Cooperative Success",
                description: "Through PEDA's agricultural training, local farmers have formed cooperatives and increased their harvest yields by 40%, improving food security and income.",
                location: "Central Uganda",
              },
              {
                image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/lxiebqhu_55d8f7e2-761d-4c63-9839-a0652352eb5a.jpeg",
                title: "Youth Entrepreneurship Exhibition",
                description: "Students trained through PEDA's TVET programs showcasing their products and business ideas at regional exhibitions, gaining market access.",
                location: "Kampala Region",
              },
              {
                image: "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/v6qw6xmc_ph_28265_101967.jpg",
                title: "School Agriculture Program",
                description: "Students learning practical agricultural skills through school garden projects, combining education with hands-on enterprise experience.",
                location: "Eastern Uganda",
              },
            ].map((story, index) => (
              <motion.div
                key={story.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl transition-all group"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={story.image} 
                    alt={story.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs text-amber-600 font-semibold">{story.location}</span>
                  <h3 className="text-lg font-semibold text-slate-900 mt-2 mb-3">{story.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{story.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Testimonials
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              What Our Community Says
            </h2>
            <p className="text-lg text-slate-600">
              Hear from the people whose lives have been transformed through PEDA programs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "PEDA's training program gave me the skills and confidence to start my own agricultural business. Today, I employ 5 people from my community.",
                name: "Participant Name",
                role: "Agriculture Training Graduate",
                location: "Central Uganda",
              },
              {
                quote: "The TVET program transformed my life. I learned practical skills that helped me secure employment immediately after completing the training.",
                name: "Participant Name",
                role: "TVET Program Graduate",
                location: "Kampala",
              },
              {
                quote: "Through PEDA's leadership program, I learned how to mobilize my community and lead development initiatives. We have since completed 3 community projects.",
                name: "Participant Name",
                role: "Community Leader",
                location: "Eastern Uganda",
              },
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm"
              >
                <Quotes className="h-10 w-10 text-amber-500 mb-4" weight="fill" />
                <p className="text-slate-700 leading-relaxed mb-6 italic">"{testimonial.quote}"</p>
                <div className="border-t border-slate-100 pt-4">
                  <p className="font-semibold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-amber-600">{testimonial.role}</p>
                  <p className="text-xs text-slate-500 mt-1">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-slate-500 mt-8 text-sm">
            * Names can be updated with actual participant testimonials
          </p>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="impact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
                What We Do
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6">
                Supporting Communities With Skills, Innovation & Growth
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                We provide valuable training in business management, financial literacy, marketing, leadership, and technology adoption while promoting community ownership and active participation.
              </p>
              
              <div className="grid gap-3">
                {impactAreas.map((area) => (
                  <div key={area} className="flex items-center gap-3 bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
                    <CheckCircle className="h-5 w-5 text-green-700 shrink-0" weight="fill" />
                    <span className="font-medium text-slate-700">{area}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-700 to-green-800 rounded-2xl p-8 text-white shadow-xl">
              <h3 className="text-2xl font-bold mb-6">Why Communities Choose PEDA</h3>
              <div className="space-y-4">
                {[
                  "Research-driven and community-centered interventions",
                  "Strong focus on entrepreneurship and inclusive growth",
                  "Practical training that builds real-world skills",
                  "Commitment to community ownership and sustainability",
                ].map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" weight="fill" />
                    <p className="text-green-50 leading-relaxed">{point}</p>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 pt-6 border-t border-green-600/50">
                <Button 
                  asChild 
                  className="w-full bg-white text-green-700 hover:bg-green-50 rounded-xl"
                  data-testid="partner-with-pade-button"
                >
                  <Link to="/contact">
                    Partner With PEDA <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ugandan Agribusiness Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="agribusiness-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Opportunities
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              Ugandan Agribusiness Potential
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Uganda's agribusiness sector is rich with opportunities driven by fertile land, favorable climate, and a growing population. PEDA supports initiatives that unlock value across the agricultural value chain.
            </p>
          </motion.div>

          <div className="mt-12 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {[
              {
                title: "Juice Production",
                text: "Leverage Uganda's abundant fresh fruit supply to produce natural, healthy beverages for growing local and export demand.",
              },
              {
                title: "Coffee & Tea",
                text: "Uganda is a leading coffee exporter in Africa with strong potential to scale production and exports, supported by national growth targets.",
              },
              {
                title: "Livestock",
                text: "Cattle, goats, and poultry sectors offer opportunities for both domestic consumption and international trade expansion.",
              },
              {
                title: "Agro-Processing",
                text: "Investing in processing infrastructure increases the value of agricultural products and strengthens export competitiveness.",
              },
              {
                title: "Cereal Crops",
                text: "Maize, millet, rice, and wheat production presents strong potential for scaling and revenue generation.",
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-50 rounded-2xl p-6 border border-slate-100"
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 rounded-2xl border border-green-100 bg-green-50 p-8">
            <p className="text-slate-700 leading-relaxed">
              These areas represent just a few of the many opportunities available in Uganda's agribusiness sector. With the right strategies and investments, businesses can tap into the country's abundant agricultural resources to drive job creation, economic growth, and sustainable development.
            </p>
          </div>
        </div>
      </section>

      {/* Latest Projects */}
      {projects.length > 0 && (
        <section className="py-20 md:py-28" data-testid="projects-preview-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
                  Our Projects
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
                  Latest Programs & Initiatives
                </h2>
              </div>
              <Button 
                asChild 
                variant="outline" 
                className="rounded-xl border-green-700 text-green-700 hover:bg-green-50"
                data-testid="view-all-projects-button"
              >
                <Link to="/projects">View All Projects</Link>
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project, index) => (
                <motion.article
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img 
                      src={project.image_url || "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/afobkmcc_SCHOOL.png"} 
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        {project.category}
                      </span>
                      <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full capitalize">
                        {project.status}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2">{project.title}</h3>
                    <p className="text-slate-600 text-sm line-clamp-3 mb-4">{project.description}</p>
                    {project.location && (
                      <p className="text-xs text-slate-500">Location: {project.location}</p>
                    )}
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Latest Blog Posts */}
      {blogPosts.length > 0 && (
        <section className="py-20 md:py-28 bg-slate-50" data-testid="blog-preview-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
                  News & Updates
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
                  Latest From Our Blog
                </h2>
              </div>
              <Button 
                asChild 
                variant="outline" 
                className="rounded-xl border-green-700 text-green-700 hover:bg-green-50"
                data-testid="view-all-blog-button"
              >
                <Link to="/blog">View All Posts</Link>
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img 
                      src={post.image_url || "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0s4xigoz_ICT.png"} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-green-700 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 text-sm line-clamp-3 mb-4">{post.excerpt}</p>
                    <p className="text-xs text-slate-500">By {post.author}</p>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-28" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-green-800 to-green-900 rounded-3xl p-8 md:p-12 lg:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 pattern-dots" />
            </div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Partner with PEDA to Build Thriving Communities
              </h2>
              <p className="text-lg text-green-100/90 mb-8 max-w-2xl mx-auto leading-relaxed">
                We believe that empowering local communities to lead their own development creates lasting and meaningful change. Let's work together to strengthen enterprises, unlock innovation, and advance prosperity across Africa.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-8"
                  data-testid="cta-contact-button"
                >
                  <Link to="/contact">
                    Contact PEDA <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
                  </Link>
                </Button>
                <Button 
                  asChild 
                  size="lg" 
                  variant="outline" 
                  className="bg-transparent border-white/30 text-white hover:bg-white/10 rounded-xl px-8"
                  data-testid="cta-projects-button"
                >
                  <Link to="/projects">View Our Projects</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donate CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-amber-500 to-amber-600" data-testid="donate-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-green-950 mb-6">
            Support Our Mission
          </h2>
          <p className="text-lg text-green-900/80 mb-8 leading-relaxed">
            Your support helps PEDA empower communities, promote entrepreneurship, and create sustainable livelihoods across Africa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-green-700 hover:bg-green-800 text-white font-semibold rounded-xl px-8"
              data-testid="donate-now-button"
            >
              <Link to="/donate">
                Donate Now <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
              </Link>
            </Button>
            <Button 
              asChild 
              size="lg" 
              variant="outline" 
              className="bg-white border-green-700 text-green-700 hover:bg-green-50 rounded-xl px-8"
              data-testid="become-partner-button"
            >
              <Link to="/partnerships">Become a Partner</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
